import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { NachaMatch } from '../../../models/nacha.model';

@Component({
  selector: 'app-nacha-review-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatButtonModule,
  ],
  templateUrl: './review-step.component.html',
})
export class NachaReviewStepComponent {
  @Input() matches: NachaMatch[] = [];
  @Output() toggleInclude = new EventEmitter<NachaMatch>();
  @Output() addCustomer = new EventEmitter<NachaMatch>();
  @Output() selectCustomer = new EventEmitter<NachaMatch>();
  // Bulk: create a customer for every "Customer not found" row.
  @Output() addAllCustomers = new EventEmitter<void>();
  // ACH account conflict resolution (account-mismatch / no-account rows).
  @Output() useNachaAccount = new EventEmitter<NachaMatch>();
  @Output() useFreelyAccount = new EventEmitter<NachaMatch>();
  @Output() updateFreelyAccount = new EventEmitter<NachaMatch>();
  @Output() saveAccountOnFile = new EventEmitter<NachaMatch>();
  // Bulk "apply to all" over every account-mismatch row.
  @Output() applyToAllMismatches = new EventEmitter<'nacha' | 'freely' | 'update'>();

  readyDisplayedColumns = [
    'include',
    'individualId',
    'customerName',
    'maskedAccount',
    'amount',
    'txType',
    'traceNumber',
    'status',
  ];
  attentionDisplayedColumns = [
    'individualId',
    'individualName',
    'amount',
    'txType',
    'accountDetail',
    'status',
    'actions',
  ];

  get readyMatches(): NachaMatch[] {
    return this.matches.filter((m) => m.status === 'matched');
  }

  /** Rows needing operator action: no customer, account mismatch, or no account on file. */
  get attentionMatches(): NachaMatch[] {
    return this.matches.filter(
      (m) => m.status === 'no-customer' || m.status === 'account-mismatch' || m.status === 'no-account'
    );
  }

  get mismatchCount(): number {
    return this.matches.filter((m) => m.status === 'account-mismatch').length;
  }

  get noCustomerCount(): number {
    return this.matches.filter((m) => m.status === 'no-customer').length;
  }

  get includedCount(): number {
    return this.matches.filter((m) => m.include && m.status === 'matched').length;
  }

  get totalReadyAmount(): number {
    return this.readyMatches.filter((m) => m.include).reduce((sum, m) => sum + m.row.amount, 0);
  }

  get debitCount(): number {
    return this.matches.filter((m) => m.row.isDebit).length;
  }

  get creditCount(): number {
    return this.matches.filter((m) => !m.row.isDebit).length;
  }

  maskAccount(account?: string): string {
    if (!account) return '****';
    const last4 = account.slice(-4);
    return `\u2022\u2022\u2022\u2022${last4}`;
  }

  /** Masked account shown in the Ready table \u2014 the account that will actually be charged. */
  chargedAccount(match: NachaMatch): string {
    if (match.accountSource === 'freely' && match.selectedToken?.account) {
      return this.maskAccount(match.selectedToken.account);
    }
    return this.maskAccount(match.row.bankAccountNumber);
  }

  /** First on-file ACH account for a mismatch row (used to show what differs). */
  onFileToken(match: NachaMatch) {
    return match.matchedToken ?? match.achTokens?.[0];
  }

  getStatusIcon(match: NachaMatch): string {
    switch (match.status) {
      case 'matched':
        return 'check_circle';
      case 'account-mismatch':
      case 'no-account':
        return 'warning';
      case 'no-customer':
        return 'error';
    }
  }

  getStatusClass(match: NachaMatch): string {
    switch (match.status) {
      case 'matched':
        return 'status-success';
      case 'account-mismatch':
      case 'no-account':
        return 'status-warning';
      case 'no-customer':
        return 'status-error';
    }
  }

  getStatusLabel(match: NachaMatch): string {
    switch (match.status) {
      case 'matched':
        return 'Ready';
      case 'account-mismatch':
        return 'Account Mismatch';
      case 'no-account':
        return 'No Account on File';
      case 'no-customer':
        return 'No Customer';
    }
  }

  onToggleInclude(match: NachaMatch): void {
    this.toggleInclude.emit(match);
  }

  onAddCustomer(match: NachaMatch): void {
    this.addCustomer.emit(match);
  }

  onSelectCustomer(match: NachaMatch): void {
    this.selectCustomer.emit(match);
  }

  onAddAllCustomers(): void {
    this.addAllCustomers.emit();
  }

  onUseNacha(match: NachaMatch): void {
    this.useNachaAccount.emit(match);
  }

  onUseFreely(match: NachaMatch): void {
    this.useFreelyAccount.emit(match);
  }

  onUpdateFreely(match: NachaMatch): void {
    this.updateFreelyAccount.emit(match);
  }

  onSaveOnFile(match: NachaMatch): void {
    this.saveAccountOnFile.emit(match);
  }

  onApplyToAll(action: 'nacha' | 'freely' | 'update'): void {
    this.applyToAllMismatches.emit(action);
  }
}
